import { createCustomFilterForEntity } from "@shared/backend/create-custom-filter-for-entity";
import {
  filterWithRelationsAndJson,
  type RelationalOrJsonEntityFilter,
} from "@shared/helpers/sql-relations";
import { dbNamesOf, SqlDatabase } from "remult";
import type { OrganizationManager } from "../organization-manager";
import { SubTeam } from "../sub-team";
import { TeamManager } from "../team-manager";
import { Team } from "./";

const createCustomFilter = createCustomFilterForEntity<Team>();

export class TeamFilters {
  /**
   * # 🌟✨ HyperFilter – The Ultimate Filtering Powerhouse!
   * ### 🤝 Filter through complex relations effortlessly
   * ### 📦 Directly filter JSON fields (no backend work required!)
   * ### 🚀 Transforms your filter into a single, optimized DB query
   * ### 🔒 Engineered for maximum SQL injection protection
   *
   * ---
   * ### Example:
   * ```ts
   * myEntityRepo.find({
   *  where: MyEntity.hyperFilter({ ... })
   * })
   * ```
   *
   * ---
   * Built by Natan Salmon
   */
  static hyperFilter = createCustomFilter<RelationalOrJsonEntityFilter<Team>>(
    (filters) => {
      return filterWithRelationsAndJson(Team)(filters);
    },
  );

  static isTeamOrAnySubTeamUnderThisTeamManagedBy = createCustomFilter<{
    manager: string | OrganizationManager;
  }>(async ({ manager }) => {
    const managerId = typeof manager === "string" ? manager : manager.id;

    const [teamManager, team, subTeam] = await Promise.all([
      dbNamesOf(TeamManager, {
        tableName: true,
      }),
      dbNamesOf(Team, {
        tableName: true,
      }),
      dbNamesOf(SubTeam, {
        tableName: true,
      }),
    ]);

    return SqlDatabase.rawFilter(({ sql, param }) => {
      // Base case: Get the team(s) managed by the given manager
      // Recursive case: Join with subTeam to find parent-child relationships
      sql = `
        EXISTS (
          WITH RECURSIVE TeamTree AS (
            SELECT ${team.id} AS id
            FROM ${team}
            WHERE EXISTS (
              SELECT 1
              FROM ${teamManager}
              WHERE ${teamManager.managerId} = ${param(managerId)} 
                AND ${teamManager.teamId} = ${team.id}
            )
          UNION ALL
            SELECT ${team.id}
            FROM ${team}
            INNER JOIN ${subTeam} ON ${subTeam.subTeamId} = ${team.id}
            INNER JOIN TeamTree ON ${subTeam.teamId} = TeamTree.id
          )
          SELECT 1
          FROM TeamTree
          WHERE TeamTree.id = ${team.id}
        )
      `;

      return sql;
    });
  });
}
